import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import logoIcon from '../../assets/logo-icon.png';
import './HeaderNew.css';

const solutions = [
  { path: '/solutions/conversational-agents', label: 'Conversational Agents', desc: 'WhatsApp, Telegram & web chat' },
  { path: '/solutions/workflow-agents', label: 'Workflow Agents', desc: 'Autonomous business processes' },
  { path: '/solutions/data-agents', label: 'Data & Research Agents', desc: 'Monitoring, extraction, reports' },
  { path: '/solutions/enterprise', label: 'Enterprise AI', desc: 'Full-stack custom systems' },
];

const navLinks = [
  { path: '/agent-studio', label: 'Agent Studio' },
  { path: '/case-studies', label: 'Case Studies' },
  { path: '/pricing', label: 'Pricing' },
];

export const HeaderNew = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSolutionsOpen(false);
  }, [location]);

  const openSolutions = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsSolutionsOpen(true);
  };
  const closeSolutions = () => {
    closeTimer.current = setTimeout(() => setIsSolutionsOpen(false), 120);
  };

  const solutionsActive = location.pathname.startsWith('/solutions');

  return (
    <header className={`header-new ${isScrolled ? 'scrolled' : ''} ${isMobileMenuOpen ? 'menu-open' : ''}`}>
      <div className="header-container">
        <Link to="/" className="header-logo" aria-label="Liceron home">
          <img src={logoIcon} alt="" className="logo-img" aria-hidden="true" />
          <span className="logo-text">LICERON</span>
        </Link>

        <nav className="header-nav" aria-label="Primary">
          <div
            className="nav-dropdown"
            onMouseEnter={openSolutions}
            onMouseLeave={closeSolutions}
          >
            <button
              className={`nav-link nav-dropdown-trigger ${solutionsActive ? 'active' : ''}`}
              aria-expanded={isSolutionsOpen}
              aria-haspopup="true"
              onClick={() => setIsSolutionsOpen((v) => !v)}
            >
              Solutions
              <ChevronDown size={15} className={`chevron ${isSolutionsOpen ? 'up' : ''}`} />
            </button>

            <AnimatePresence>
              {isSolutionsOpen && (
                <motion.div
                  className="dropdown-panel"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                  onMouseEnter={openSolutions}
                  onMouseLeave={closeSolutions}
                >
                  {solutions.map((s) => (
                    <Link key={s.path} to={s.path} className="dropdown-item">
                      <span className="dropdown-item-label">{s.label}</span>
                      <span className="dropdown-item-desc">{s.desc}</span>
                    </Link>
                  ))}
                  <Link to="/solutions" className="dropdown-viewall">
                    View all solutions <ArrowRight size={14} />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <Link to="/contact" className="btn-get-started">
            Get Started <ArrowRight size={15} />
          </Link>
          <button
            className="mobile-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="mobile-section-label">Solutions</span>
            {solutions.map((s) => (
              <Link key={s.path} to={s.path} className="mobile-link mobile-sub">
                {s.label}
              </Link>
            ))}
            <div className="mobile-divider" />
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} className="mobile-link">
                {link.label}
              </Link>
            ))}
            <Link to="/contact" className="mobile-cta">
              Get Started
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
